/**
 * AN EPIC'S STANDING, COMPUTED FROM ITS CARDS — P2 #2, the Epics-as-object work.
 *
 * An epic is not a record that is filled in and then drifts out of date: it is
 * the cards that carry its name, read together. Owner, health, progress and the
 * due date are all DERIVED here from the member cards, never typed and stored
 * alongside them — so the roll-up can never disagree with the board it describes
 * (the standing "data must be derived, not re-keyed" rule). The one thing a
 * person sets by hand is an OVERRIDE (owner/due/health committed on Edit epic),
 * which lands with the epic-metadata table in a later step and layers on top of
 * what this computes; until then every screen reads purely what is here.
 *
 * KEYED BY NAME, not by an id. A card keeps its `epic` string exactly as it is;
 * the epic list is the distinct names across the cards, each name grouped to its
 * members. This is deliberate — it is why a tracker push that mentions an epic
 * can never "lose" it: there is no separate row to drop, the name on the card IS
 * the membership.
 *
 * SCOPE OF THIS CUT. The board roll-up (rollUpBoardEpics, one board's cards) and
 * the WORKSPACE roll-up (rollUpWorkspaceEpics, cards across every board the
 * Portfolio loaded) are both derived-fields-only. The cross-board span IS carried
 * here now — the Portfolio reads every board's cards, so an epic that rides on
 * several boards rolls up across all of them, and sits in its lifecycle band.
 * Class (Run vs Change), the description and the committed due override still need
 * data this branch does not carry (#435 and the epic-metadata migration), and are
 * added where they land; nothing here pretends to them.
 */
import type { Issue, IssueSize, IssueStatus, StatusUpdate } from './types';
import { SIZE_POINTS, isInProcess } from './types';
import { blockerOf } from './blocked';
import { cardDueState } from './phases';
import { epicTint, type EpicTint } from './epics';
import { epicLifecycleTier, type LifecycleTier } from './lifecycle';

/** Green / amber / red, worst-signal-wins across the member cards. */
export type EpicHealth = 'ontrack' | 'atrisk' | 'offtrack';

export interface EpicRollup {
  /** The epic's name — the string the cards carry, the key for everything. */
  name: string;
  /** Stable colour chip, resolved against every epic name on the board. */
  tint: EpicTint | null;
  /** The email of the person holding the plurality of member cards; '' if none. */
  owner: string;
  health: EpicHealth;
  /** A short, plain reason for the health — the card's own blocker if there is
   *  one and only one, else a count; '' when on track. */
  healthReason: string;
  /** Size-weighted, GRANULAR completion 0–100: a done card counts in full, an open
   *  card counts the share it has recorded (manual %, or its ticked-off definition
   *  of done). Reaches 100 only when every counted card is done. See weighting note. */
  progressPct: number;
  doneCount: number;
  openCount: number;
  totalCount: number;
  /** The latest due date across the still-open cards (ISO), or null if none. */
  dueAuto: string | null;
  /** How that date reads against today — overdue / due-soon / clear. */
  dueState: 'overdue' | 'due-soon' | 'clear';
  /** Cards currently blocked OR past their due date — the actionable set. */
  needsAttention: number;
  /** When the epic's first card was created (ms), or null if it has no cards. */
  startedMs: number | null;
  /** Member cards created AFTER the first one — the "+N since <start>" scope line.
   *  A zero-friction baseline (the epic's first card), not a committed baseline. */
  scopeAdded: number;
}

/** The newest status-log entry across an epic's cards — what the epic header's
 *  read-only "Latest update" line shows. Auto lines (bare status moves the app
 *  wrote) are skipped; a human update or note is what "latest update" means. */
export interface EpicLatestUpdate {
  issueId: string;
  ref: string;
  at: number;
  by: string;
  text: string;
  nextStep?: string;
}

const isResolved = (s: IssueStatus) => s === 'done' || s === 'wontdo';
const isBlocked = (i: Issue) => blockerOf(i) !== '';

/**
 * A card's weight for size-weighting. A set size uses its points (1/3/8); an
 * unsized card weighs 1, so an epic whose cards are all unsized falls back to a
 * straight card count — the behaviour the spec asks for at that boundary — and a
 * part-sized epic degrades smoothly between the two rather than ignoring the
 * sizes it does have.
 */
function weightOf(size: IssueSize | '' | undefined): number {
  return size ? SIZE_POINTS[size] : 1;
}

/** How much of a card to count toward completion: a done card in full, an open
 *  card the share it has recorded, capped JUST below full so no unfinished card
 *  can read as done. `pct` is null — or a malformed NaN — when the card records
 *  no usable progress, and counts as 0 then (never poisons the weighted sum). */
function fractionDone(status: IssueStatus, pct: number | null): number {
  if (status === 'done') return 1;
  const v = pct == null || Number.isNaN(pct) ? 0 : pct;
  return Math.min(Math.max(v / 100, 0), 0.99);
}

/** Round the weighted completion, but hold it below 100 unless EVERY counted
 *  (non-wontdo) card is actually done — the "bar hits 100 iff it is done"
 *  invariant the phase bar keeps too. Guards every non-finite input (a NaN datum,
 *  an empty epic) so a sponsor-facing roll-up can never print "NaN%". */
function cappedPct(doneWeight: number, totalWeight: number, counted: number, done: number): number {
  if (!(totalWeight > 0) || !Number.isFinite(doneWeight)) return 0;
  if (counted > 0 && done === counted) return 100;
  const pct = Math.round((doneWeight / totalWeight) * 100);
  return Number.isFinite(pct) ? Math.min(pct, 99) : 0;
}

/** The default way to read a card's progress for the roll-up: its typed %, or
 *  none. A malformed NaN counts as none (the same guard effectiveProgress and
 *  progressHealth keep, so every surface agrees and none prints "NaN%"). A caller
 *  with the board's definition of done in scope passes a resolver that also
 *  derives a % from ticked checks (see src/progress-derive.ts). */
const typedProgress = (c: Issue): number | null => {
  const p = c.progressPct;
  return p == null || Number.isNaN(p) ? null : p;
};

/** Later ISO date wins; nulls lose. Dates are 'YYYY-MM-DD', so string compare is date order. */
function laterDue(a: string | null, b: string | null | undefined): string | null {
  if (!b) return a;
  if (!a) return b;
  return b > a ? b : a;
}

/**
 * An epic's size-weighted completion as a WEEKLY BACK-SERIES — the last `weeks`
 * weeks, each value the % done AS OF that week's end. Insights reads this to say
 * whether an epic is moving or has stalled, which is a MEASURE (a trend), never
 * the action view the Epics screen owns.
 *
 * It is point-in-time and derived only from the cards' own timestamps, so it
 * needs no stored history (which has gaps): a card counts toward the denominator
 * from its `createdAt`, and toward the numerator once `completedAt` has passed
 * (the same clock throughput uses, falling back to `updatedAt` for cards closed
 * before that field existed). This trend is DONE-ONLY — a card is in the numerator
 * only once it is finished, never for partial progress — so it no longer tracks
 * `rollUpEpic(...).progressPct` step for step now that the roll-up also counts the
 * share an OPEN card has recorded. The two coincide only when no card carries
 * partial progress; the trend deliberately stays a finished-work series, because a
 * back-series of estimated partials is not something the timestamps can honestly
 * reconstruct.
 *
 * WHY THEY CAN. `progressPct` never consults `now`: a card counts as done because
 * its status says so. This series does, twice — a card is in the denominator only
 * once `createdAt` has passed, and in the numerator only once `doneAt` has. And
 * `completedAt` is written by the DATABASE (migration 0096) while `now` is the
 * browser's own clock, so a machine running a few minutes behind sees a
 * just-finished card counted by the headline and not yet by the trend. The gap
 * closes by itself as the clock catches up; it is a reporting lag, not a
 * disagreement about the work. `test-epic-rollup.ts` pins both directions.
 *
 * Two honest approximations,
 * because point-in-time STATUS is not stored: a card abandoned or reopened later
 * is treated by its CURRENT status throughout (wontdo is excluded in every week,
 * a reopened card stays counted by its latest completion) — the same simplification
 * the rest of Insights makes.
 */
export function epicCompletionSeries(cards: Issue[], now: number, weeks = 8): number[] {
  const DAY = 86_400_000;
  const doneAt = (c: Issue) => c.completedAt ?? c.updatedAt ?? 0;
  const series: number[] = [];
  for (let w = weeks - 1; w >= 0; w--) {
    const end = now - w * 7 * DAY;
    let totalWeight = 0;
    let doneWeight = 0;
    for (const c of cards) {
      if (c.createdAt > end) continue; // not yet created at this week's end
      if (c.status === 'wontdo') continue; // abandoned work is out of the goal, as the roll-up has it
      const wt = weightOf(c.size);
      totalWeight += wt;
      if (c.status === 'done' && doneAt(c) <= end) doneWeight += wt;
    }
    series.push(totalWeight ? Math.round((doneWeight / totalWeight) * 100) : 0);
  }
  return series;
}

/**
 * The share of an epic's OPEN cards that must be troubled (blocked or past due)
 * before it reads off track rather than at risk — the proportional half of the
 * health rule (PM-G1). Half: an epic whose open work is mostly stalled is off
 * track; a minority in trouble is at risk. A module constant so the one number
 * that decides a sponsor-facing colour is named, findable and tunable in one place.
 */
const RED_SHARE = 0.5;

/**
 * Roll ONE epic up from the cards that carry its name.
 *
 * `allEpicNames` is every epic name on the board, needed only so the colour chip
 * resolves the same way it does on the board and the card (epicTint walks the
 * whole set to break collisions).
 */
export function rollUpEpic(
  name: string,
  cards: Issue[],
  allEpicNames: string[],
  now = Date.now(),
  progressOf: (c: Issue) => number | null = typedProgress,
): EpicRollup {
  let doneWeight = 0;
  let totalWeight = 0;
  let counted = 0; // non-wontdo cards — the completion denominator by count
  let doneCount = 0;
  let openCount = 0;
  let blockedCount = 0;
  let pastDueCount = 0;
  let troubledCount = 0; // distinct OPEN cards that are blocked OR past due
  let stuck = false; // a card both past-due AND blocked — the acute red case
  let dueAuto: string | null = null;
  let loneBlockReason = '';
  let startedMs: number | null = null;
  const ownerTally = new Map<string, number>();

  for (const c of cards) {
    if (startedMs === null || c.createdAt < startedMs) startedMs = c.createdAt;
    const resolved = isResolved(c.status);
    /*
     * A FINISHED CARD IS NOT BLOCKED, whatever its blocker field still says.
     * Gated the same way `overdue` is on the line below, and for the same
     * reason: a blocker is a sentence somebody typed (src/blocked.ts) and
     * nothing clears it when the card is closed, so without this an epic whose
     * every card was done reported 100% complete, nothing needing attention,
     * and 'at risk' quoting a finished card's words. needsAttention already
     * gated on !isResolved; health did not, and the two disagreed.
     */
    const blocked = !resolved && isBlocked(c);
    const overdue = !resolved && cardDueState(c.dueDate, c.status, now) === 'overdue';

    // Progress — size-weighted and GRANULAR now: a done card counts in full, an
    // open card counts the share it records (typed %, or its ticked definition of
    // done via the injected resolver); cancelled work (won't do) left out of both.
    if (c.status !== 'wontdo') {
      const w = weightOf(c.size);
      totalWeight += w;
      counted += 1;
      doneWeight += w * fractionDone(c.status, progressOf(c));
    }

    if (c.status === 'done') doneCount++;
    if (!resolved) {
      openCount++;
      dueAuto = laterDue(dueAuto, c.dueDate); // the latest still-open due date
      if (c.assignee) ownerTally.set(c.assignee, (ownerTally.get(c.assignee) ?? 0) + 1);
    }

    if (blocked) {
      blockedCount++;
      loneBlockReason = blockerOf(c);
    }
    if (overdue) pastDueCount++;
    if (blocked || overdue) troubledCount++;
    if (blocked && overdue) stuck = true;
  }

  /*
   * Health — proportional, and Red is REACHABLE. The old rule made Red require a
   * single card to be BOTH blocked AND past due — rare, so in practice nothing
   * ever read off track, and ANY blocked-or-overdue card read at risk. Live that
   * came out 41 epics → 28 at risk / 13 on track / 0 off track: amber saturated
   * to the point of carrying no information, the single most sponsor-facing
   * number telling you nothing (PM-G1). Now:
   *   green — nothing open is blocked or past due
   *   amber — something is, but it is one card, or a minority of the open work
   *   red   — a card is STUCK (blocked AND past due — acute on its own), OR a
   *           PATTERN of trouble: two or more troubled cards that are at least
   *           half the open work. So one laggard in a big epic does not shout,
   *           and an epic whose open work is mostly stalled cannot hide in amber.
   * Red reads off an OR (blocked or past due), never the old unreachable AND.
   */
  const troubledShare = openCount > 0 ? troubledCount / openCount : 0;
  let health: EpicHealth = 'ontrack';
  if (troubledCount > 0) {
    const offTrack = stuck || (troubledCount >= 2 && troubledShare >= RED_SHARE);
    health = offTrack ? 'offtrack' : 'atrisk';
  }

  // A plain reason, in the mockup's register: the one blocked card's own words
  // when it is the only one, else a count; past-due when nothing is blocked.
  let healthReason = '';
  if (blockedCount === 1) healthReason = loneBlockReason || '1 card blocked';
  else if (blockedCount > 1) healthReason = `${blockedCount} cards blocked`;
  else if (pastDueCount === 1) healthReason = '1 card past due';
  else if (pastDueCount > 1) healthReason = `${pastDueCount} cards past due`;

  // Plurality owner: most open cards; a tie falls to the alphabetically-first
  // address, so the same cards always name the same owner.
  let owner = '';
  let best = 0;
  for (const [who, n] of [...ownerTally].sort((a, b) => a[0].localeCompare(b[0]))) {
    if (n > best) {
      best = n;
      owner = who;
    }
  }

  return {
    name,
    tint: epicTint(name, allEpicNames),
    owner,
    health,
    healthReason,
    progressPct: cappedPct(doneWeight, totalWeight, counted, doneCount),
    doneCount,
    openCount,
    totalCount: cards.length,
    dueAuto,
    dueState: dueAuto ? cardDueState(dueAuto, 'inprogress', now) : 'clear',
    // Counts from To-Do up — a backlog card is a parked idea, never "needs attention"
    // (Vinnie, 3 Nov); matches needs-attention.ts, which this figure reconciles against.
    needsAttention: cards.filter((c) => isInProcess(c.status) && (isBlocked(c) || cardDueState(c.dueDate, c.status, now) === 'overdue')).length,
    startedMs,
    scopeAdded: startedMs === null ? 0 : cards.filter((c) => c.createdAt > startedMs!).length,
  };
}

/**
 * The newest human status-log entry across an epic's cards — the epic header's
 * read-only "Latest update". Auto lines (bare status moves) are skipped; of the
 * rest the latest by time wins, a tie broken by card order (the order `cards`
 * arrives in), so the same cards always surface the same line. `null` when no
 * card has ever had an update.
 */
export function latestUpdateAcross(cards: Issue[]): EpicLatestUpdate | null {
  let best: EpicLatestUpdate | null = null;
  for (const c of cards) {
    for (const e of (c.standsLog ?? []) as StatusUpdate[]) {
      if (e.auto) continue;
      if (!e.text?.trim()) continue;
      // Strictly-greater keeps the earliest card on a tie (card order).
      if (best === null || e.at > best.at) {
        best = { issueId: c.id, ref: c.ref, at: e.at, by: e.by, text: e.text, nextStep: e.nextStep };
      }
    }
  }
  return best;
}

export interface BoardEpics {
  /** One roll-up per distinct epic on the board, most open work first. */
  epics: EpicRollup[];
  /** The cards carrying no epic — a real bucket, not a gap to hide. */
  noEpic: { total: number; open: number };
  /** Every card considered (for the "of N total" line). */
  totalCards: number;
  /** Share of OPEN cards that sit under some epic, 0–100 — the "grouped" line. */
  groupedPct: number;
}

/**
 * Roll every epic on one board up from its cards.
 *
 * An epic name is taken verbatim as the card carries it; grouping is
 * case-sensitive on purpose (the card's own spelling is the truth, and two
 * spellings are two epics until somebody reconciles them on the cards).
 */
export function rollUpBoardEpics(
  cards: Issue[],
  now = Date.now(),
  progressOf: (c: Issue) => number | null = typedProgress,
): BoardEpics {
  const byName = new Map<string, Issue[]>();
  let noEpicTotal = 0;
  let noEpicOpen = 0;
  let openTotal = 0;
  let openGrouped = 0;

  for (const c of cards) {
    const open = !isResolved(c.status);
    if (open) openTotal++;
    const name = (c.epic ?? '').trim();
    if (!name) {
      noEpicTotal++;
      if (open) noEpicOpen++;
      continue;
    }
    if (open) openGrouped++;
    const list = byName.get(name);
    if (list) list.push(c);
    else byName.set(name, [c]);
  }

  const allNames = [...byName.keys()];
  const epics = allNames
    .map((name) => rollUpEpic(name, byName.get(name)!, allNames, now, progressOf))
    .sort((a, b) => b.openCount - a.openCount || a.name.localeCompare(b.name));

  return {
    epics,
    noEpic: { total: noEpicTotal, open: noEpicOpen },
    totalCards: cards.length,
    groupedPct: openTotal ? Math.round((openGrouped / openTotal) * 100) : 0,
  };
}

/** One board an epic has cards on — its name and how many of the epic's cards sit
 *  there, so the lens can read "3 boards" and name them, plurality first. */
export interface EpicBoard {
  id: string;
  name: string;
  cards: number;
}

/** An epic as the whole workspace sees it: the same derived roll-up, computed
 *  across EVERY board's cards, plus the boards it rides on and the lifecycle band
 *  it sits in (the tier of the board holding the plurality of its cards). */
export interface WorkspaceEpic extends EpicRollup {
  /** The boards this epic has cards on, most cards first then by name. */
  boards: EpicBoard[];
  /** The band it sits in — Pipeline / In delivery / In service — or null if its
   *  boards carry no tier (should not happen once every board has one). */
  lifecycleTier: LifecycleTier | null;
}

/** What the caller tells the workspace roll-up about each board: its name and the
 *  lifecycle tier it has already derived (the Portfolio owns the phase/go-live
 *  machinery, so it computes the tier and hands it in — this file stays pure). */
export interface BoardTier {
  id: string;
  name: string;
  tier: LifecycleTier;
}

export interface WorkspaceEpics {
  /** One roll-up per distinct epic name across the workspace, most open work first. */
  epics: WorkspaceEpic[];
  /** Every card that carries no epic, across all boards — the honest bucket. */
  noEpic: { total: number; open: number };
  /** Every card considered, across all boards. */
  totalCards: number;
}

/**
 * Roll every epic in the workspace up from the cards on every board.
 *
 * Grouping is by the card's `epic` NAME across boards (the same key the board
 * roll-up uses) — so one epic whose cards live on three boards is ONE row here,
 * rolled up across all three, not three rows. The derived owner/health/progress/
 * due read exactly as rollUpEpic computes them over the combined cards; the extra
 * two fields are the boards the epic touches and its lifecycle band.
 *
 * `boards` carries each board's name and pre-derived tier (see BoardTier). A card
 * on a board the caller did not list still counts toward the roll-up, but its
 * board cannot be named or tiered — the caller passes a tier for every board it
 * handed cards for, so this is only a guard, not a normal path.
 */
export function rollUpWorkspaceEpics(
  cardsByBoard: Map<string, Issue[]>,
  boards: BoardTier[],
  now = Date.now(),
  progressOf: (c: Issue) => number | null = typedProgress,
): WorkspaceEpics {
  const meta = new Map(boards.map((b) => [b.id, b]));
  const byName = new Map<string, Issue[]>();
  // epic name → board id → how many of the epic's cards sit on that board
  const boardsByEpic = new Map<string, Map<string, number>>();
  let noEpicTotal = 0;
  let noEpicOpen = 0;
  let totalCards = 0;

  for (const [boardId, cards] of cardsByBoard) {
    for (const c of cards) {
      totalCards++;
      const name = (c.epic ?? '').trim();
      if (!name) {
        noEpicTotal++;
        if (!isResolved(c.status)) noEpicOpen++;
        continue;
      }
      const list = byName.get(name);
      if (list) list.push(c);
      else byName.set(name, [c]);
      let bm = boardsByEpic.get(name);
      if (!bm) boardsByEpic.set(name, (bm = new Map()));
      bm.set(boardId, (bm.get(boardId) ?? 0) + 1);
    }
  }

  const allNames = [...byName.keys()];
  const epics: WorkspaceEpic[] = allNames.map((name) => {
    const cards = byName.get(name)!;
    const roll = rollUpEpic(name, cards, allNames, now, progressOf);
    const counts = boardsByEpic.get(name)!;
    const epicBoards: EpicBoard[] = [...counts]
      .map(([id, n]) => ({ id, name: meta.get(id)?.name ?? '—', cards: n }))
      .sort((a, b) => b.cards - a.cards || a.name.localeCompare(b.name));
    // The band — the tier of the board holding the plurality of the epic's cards.
    // One tier per card so the plurality is by cards, matching the derived owner;
    // a board with no known tier contributes nothing (guard, see the doc).
    const tiers: LifecycleTier[] = [];
    for (const [id, n] of counts) {
      const t = meta.get(id)?.tier;
      if (t) for (let i = 0; i < n; i++) tiers.push(t);
    }
    return { ...roll, boards: epicBoards, lifecycleTier: epicLifecycleTier(tiers) };
  });
  // Most open work first, then name — the same order the board list uses.
  epics.sort((a, b) => b.openCount - a.openCount || a.name.localeCompare(b.name));

  return { epics, noEpic: { total: noEpicTotal, open: noEpicOpen }, totalCards };
}
